import * as Papa from "papaparse";
import { useEffect, useMemo, useState } from "react";
import { MAX_DOCUMENT_SIZE } from "../../shared/attachmentPolicy";
import { type CsvData, filterCsvRows, paginate } from "./csvPreviewData";
import { TableLoading, TablePreview, TableStatus } from "./TablePreview";

const CSV_PAGE_SIZE = 50;

type CsvState =
  | { status: "idle" | "loading" | "too-large" | "missing-source" }
  | { status: "error"; message: string }
  | { status: "ready"; data: CsvData };

function formatBytes(bytes: number) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function csvCell(value: unknown) {
  if (value == null) return "";
  return String(value);
}

function csvColumns(row: string[]) {
  const seen = new Map<string, number>();
  return row.map((raw, i) => {
    const base = raw.trim() || `Column ${i + 1}`;
    const count = seen.get(base) ?? 0;
    seen.set(base, count + 1);
    return count === 0 ? base : `${base} ${count + 1}`;
  });
}

function csvTableName(fileName: string | undefined) {
  if (!fileName) return "CSV";
  const base = fileName.split("/").pop() ?? fileName;
  const dot = base.lastIndexOf(".");
  return dot > 0 ? base.slice(0, dot) : base;
}

function parseCsv(text: string): CsvData {
  const parsed = Papa.parse<string[]>(text, {
    skipEmptyLines: true,
  });
  if (parsed.errors.length > 0) {
    throw new Error(parsed.errors[0]?.message ?? "Unable to parse CSV.");
  }

  const rows = parsed.data.map((row) => row.map(csvCell));
  const header = rows[0] ?? [];
  const columns = csvColumns(header);
  return {
    columns,
    rows: rows.slice(1),
  };
}

function openUrl(url: string) {
  return new URL(url, location.href).href;
}

function csvErrorMessage(err: unknown) {
  if (err instanceof Error) return err.message;
  return String(err);
}

function useCsvData({
  enabled,
  sizeBytes,
  url,
}: {
  enabled: boolean;
  sizeBytes: number;
  url: string | null;
}) {
  const [state, setState] = useState<CsvState>({ status: "idle" });

  useEffect(() => {
    if (!enabled) return;
    if (sizeBytes > MAX_DOCUMENT_SIZE) {
      // oxlint-disable-next-line react/react-compiler -- blocks React Compiler; remove when fixed
      setState({ status: "too-large" });
      return;
    }
    if (!url) {
      setState({ status: "missing-source" });
      return;
    }

    let cancelled = false;
    const controller = new AbortController();
    setState({ status: "loading" });

    fetch(openUrl(url), {
      credentials: "include",
      signal: controller.signal,
    })
      .then((res) => {
        if (!res.ok)
          throw new Error(`File read failed with HTTP ${res.status}.`);
        return res.text();
      })
      .then((text) => parseCsv(text))
      .then((data) => {
        if (!cancelled) setState({ status: "ready", data });
      })
      .catch((err) => {
        if (cancelled || controller.signal.aborted) return;
        setState({ status: "error", message: csvErrorMessage(err) });
      });

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [enabled, sizeBytes, url]);

  return state;
}

export function CsvPreview({
  enabled,
  sizeBytes,
  url,
  fileName,
}: {
  enabled: boolean;
  sizeBytes: number;
  url: string | null;
  fileName?: string;
}) {
  const state = useCsvData({ enabled, sizeBytes, url });
  const [search, setSearch] = useState("");
  const [searchColumn, setSearchColumn] = useState<string | null>(null);
  const [page, setPage] = useState(0);

  const data = state.status === "ready" ? state.data : null;
  const filteredRows = useMemo(
    () => (data ? filterCsvRows(data, search, searchColumn) : []),
    [data, search, searchColumn]
  );

  useEffect(() => {
    // oxlint-disable-next-line react/react-compiler -- blocks React Compiler; remove when fixed
    setPage(0);
  }, [data, search, searchColumn]);

  if (state.status === "idle" || state.status === "loading")
    return <TableLoading />;
  if (state.status === "missing-source")
    return <TableStatus>CSV preview needs file data.</TableStatus>;
  if (state.status === "too-large")
    return (
      <TableStatus>
        CSV preview supports files up to {formatBytes(MAX_DOCUMENT_SIZE)}.
      </TableStatus>
    );
  if (state.status === "error")
    return <TableStatus>Unable to open CSV: {state.message}</TableStatus>;
  if (!data || data.columns.length === 0)
    return <TableStatus>No columns found.</TableStatus>;

  const tableName = csvTableName(fileName);
  const { rows, offset, hasPrevious, hasNext } = paginate(
    filteredRows,
    page,
    CSV_PAGE_SIZE
  );

  return (
    <TablePreview
      tables={[{ name: tableName, icon: "table" }]}
      activeTable={tableName}
      onSelectTable={() => {}}
      searchColumns={data.columns}
      searchColumn={searchColumn}
      onSearchColumn={(name) => {
        setSearchColumn(name);
        setPage(0);
      }}
      search={search}
      onSearch={(value) => {
        setSearch(value);
        setPage(0);
      }}
      columns={data.columns.map((name) => ({ name }))}
      rows={rows.map((row, rowIndex) => ({
        key: `${offset + rowIndex}:${row.join("|")}`,
        values: Object.fromEntries(
          data.columns.map((name, columnIndex) => [
            name,
            row[columnIndex] ?? "",
          ])
        ),
      }))}
      status={
        rows.length === 0 ? (
          <TableStatus>No rows found.</TableStatus>
        ) : undefined
      }
      page={page}
      hasPrevious={hasPrevious}
      hasNext={hasNext}
      onPage={setPage}
    />
  );
}
